import React from 'react';
import { Button } from '@vibe/core';
import { useApprovalStore } from '../../stores/approvalStore';
import type { TimesheetSubmission } from '../../types/timesheet-approval.types';

export interface TimesheetSubmitButtonProps {
  submission: TimesheetSubmission | null;
  currentUserId: string;
  isAdmin: boolean;
}

/**
 * Submit / Resubmit button shown in the timesheet TopBar.
 *
 * Button label and availability depend on the current submission status:
 * - No submission: "Submit for Approval"
 * - Rejected/Reopened: "Resubmit for Approval"
 * - Submitted: disabled (already pending)
 * - Approved: hidden (locked)
 */
const TimesheetSubmitButton: React.FC<TimesheetSubmitButtonProps> = ({
  submission,
  currentUserId,
  isAdmin,
}) => {
  const setSubmitDialogOpen = useApprovalStore((s) => s.setSubmitDialogOpen);

  // Determine visibility and label
  if (submission?.status === 'approved') {
    return null; // Locked — no submit action
  }

  if (submission?.status === 'submitted') {
    return (
      <Button
        size={Button.sizes.SMALL}
        kind={Button.kinds.SECONDARY}
        disabled
      >
        Pending Approval
      </Button>
    );
  }

  const isSubmitter =
    !submission || currentUserId === submission.userId || isAdmin;
  if (!isSubmitter) return null;

  const isResubmit =
    submission?.status === 'rejected' || submission?.status === 'reopened';

  return (
    <Button
      size={Button.sizes.SMALL}
      kind={Button.kinds.PRIMARY}
      onClick={() => setSubmitDialogOpen(true)}
    >
      {isResubmit ? 'Resubmit for Approval' : 'Submit for Approval'}
    </Button>
  );
};

export default TimesheetSubmitButton;
